import { DateTime } from 'luxon'
import { EmployeeSkill, EmployeeSkillDto, employeeSkillFromDto } from '../../employee_skills/models/employeeSkill'
import { User, UserJson } from '../../users/models/user'
import { Role, roleFromDto, RoleJson } from '../../permissions/models/role'
import { z } from 'zod'
import type { JobTitleID } from '../../job_titles/models/jobTitle'
import { JobRole, jobRoleFromDto, JobRoleID } from '../../job_roles/models/jobRole'
import type { JobRole as JobRoleDto } from '../../shared/api/generated/models/jobRole'

export const EmployeeID = z.string().brand<'EmployeeID'>()
export type EmployeeID = z.infer<typeof EmployeeID>

export const EmployeeSchema = z.object({
    id: z.string(),
    display_name: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    email: z.string(),
    avatar_url: z.string().optional(),
    email_verified_at: z.string().nullable().optional(),
    contact_number_verified_at: z.string().nullable().optional(),
    sms_opted_in_at: z.string().nullable().optional(),
    archived_at: z.string().nullable(),
    custom_fields: z.record(z.string(), z.string()).optional(),
    hire_date: z.string().nullable().optional(),
    last_active_at: z.string().nullable().optional(),
    new_app_login_at: z.string().nullable().optional(),
    has_token: z.boolean().optional(),
    user_id: z.string().nullable().optional(),
    job_title_id: z.string().nullable().optional(),
    default_job_role_id: z.string().nullable().optional(),
}).transform((obj) => {
    return {
        id: obj.id as EmployeeID,
        displayName: obj.display_name,
        firstName: obj.first_name,
        lastName: obj.last_name,
        email: obj.email,
        avatarUrl: obj.avatar_url,
        emailVerifiedAt: obj.email_verified_at ? DateTime.fromISO(obj.email_verified_at) : null,
        contactNumberVerifiedAt: obj.contact_number_verified_at ? DateTime.fromISO(obj.contact_number_verified_at) : null,
        smsOptedInAt: obj.sms_opted_in_at ? DateTime.fromISO(obj.sms_opted_in_at) : null,
        archivedAt: obj.archived_at ? DateTime.fromISO(obj.archived_at) : null,
        customFields: obj.custom_fields ?? {},
        hireDate: obj.hire_date ? DateTime.fromISO(obj.hire_date) : null,
        lastActiveAt: obj.last_active_at ? DateTime.fromISO(obj.last_active_at) : null,
        newAppLoginAt: obj.new_app_login_at ? DateTime.fromISO(obj.new_app_login_at) : null,
        hasToken: obj.has_token ?? false,
        userId: obj.user_id,
        jobTitleId: (obj.job_title_id ?? null) as JobTitleID | null,
        defaultJobRoleId: (obj.default_job_role_id ?? null) as JobRoleID | null,
    }
})

type EmployeeSkillsRelation = {
    data: EmployeeSkillDto[]
} | null

type EmployeeUserRelation = {
    data: UserJson | null
} | null

type EmployeeRolesRelation = {
    data: RoleJson[]
} | null

type EmployeeDefaultJobRoleRelation = {
    data: JobRoleDto
} | null

export interface EmployeeJson {
    id: string
    display_name: string
    first_name: string
    last_name: string
    email: string

    avatar_url?: string

    email_verified_at?: string
    contact_number_verified_at?: string

    sms_opted_in_at?: string

    roles?: EmployeeRolesRelation

    has_account?: boolean

    employee_skills?: EmployeeSkillsRelation

    archived_at: string | null

    custom_fields?: Record<string, string>

    user?: EmployeeUserRelation

    hire_date?: string | null

    last_active_at?: string | null

    new_app_login_at?: string | null

    has_token?: boolean

    user_id: string | null

    job_title_id?: string | null

    default_job_role_id?: string | null

    default_job_role?: EmployeeDefaultJobRoleRelation
}

export interface Employee {
    id: EmployeeID
    displayName: string
    firstName: string
    lastName: string
    email: string

    avatarUrl?: string

    emailVerifiedAt: DateTime | null
    contactNumberVerifiedAt: DateTime | null

    smsOptedInAt: DateTime | null

    roles: Role[]

    hasAccount: boolean

    employeeSkills?: readonly EmployeeSkill[]

    archivedAt: DateTime | null

    customFields: Readonly<Record<string, string>>
    user?: User | null

    hireDate: DateTime | null

    lastActiveAt: DateTime | null

    newAppLoginAt?: DateTime | null

    hasToken: boolean

    userId: string | null

    jobTitleId: JobTitleID | null

    /** One of their effective job roles, or null when they hold none */
    defaultJobRoleId: JobRoleID | null

    /** The default job role itself, when the API included it */
    defaultJobRole?: JobRole | null
}

export const createEmployee = (
    params: {
        id: EmployeeID
        displayName: string
        firstName: string
        lastName: string
        email: string
        avatarUrl?: string
        emailVerifiedAt: DateTime | null
        contactNumberVerifiedAt: DateTime | null
        smsOptedInAt: DateTime | null
        roles: Role[]
        hasAccount: boolean
        employeeSkills?: EmployeeSkill[]
        archivedAt: DateTime | null
        customFields: Record<string, string>
        user?: User | null
        hireDate: DateTime | null
        lastActiveAt: DateTime | null
        newAppLoginAt?: DateTime | null
        hasToken: boolean
        userId: string | null
        jobTitleId?: JobTitleID | null
        defaultJobRoleId?: JobRoleID | null
        defaultJobRole?: JobRole | null
    }
): Employee => ({
    id: params.id,
    displayName: params.displayName,
    firstName: params.firstName,
    lastName: params.lastName,
    email: params.email,
    avatarUrl: params.avatarUrl,
    emailVerifiedAt: params.emailVerifiedAt,
    contactNumberVerifiedAt: params.contactNumberVerifiedAt,
    smsOptedInAt: params.smsOptedInAt,
    roles: params.roles,
    hasAccount: params.hasAccount,
    employeeSkills: params.employeeSkills,
    archivedAt: params.archivedAt,
    customFields: params.customFields,
    user: params.user,
    hireDate: params.hireDate,
    lastActiveAt: params.lastActiveAt,
    newAppLoginAt: params.newAppLoginAt,
    hasToken: params.hasToken,
    userId: params.userId,
    jobTitleId: params.jobTitleId ?? null,
    defaultJobRoleId: params.defaultJobRoleId ?? null,
    defaultJobRole: params.defaultJobRole,
})

export const employeeFromDto = (dto: EmployeeJson): Employee => (createEmployee({
    id: dto.id as EmployeeID,
    displayName: dto.display_name,
    firstName: dto.first_name,
    lastName: dto.last_name,
    email: dto.email,
    avatarUrl: dto.avatar_url,
    emailVerifiedAt: dto.email_verified_at ? DateTime.fromISO(dto.email_verified_at) : null,
    contactNumberVerifiedAt: dto.contact_number_verified_at ? DateTime.fromISO(dto.contact_number_verified_at) : null,
    smsOptedInAt: dto.sms_opted_in_at ? DateTime.fromISO(dto.sms_opted_in_at) : null,
    roles: dto.roles?.data ? dto.roles.data.map((role) => roleFromDto(role)) : [],
    hasAccount: dto.has_account ?? false,
    employeeSkills: dto.employee_skills ? dto.employee_skills.data.map((employeeSkill) => employeeSkillFromDto(employeeSkill)) : [],
    archivedAt: dto.archived_at ? DateTime.fromISO(dto.archived_at) : null,
    customFields: dto.custom_fields ?? {},
    user: dto.user?.data ? User.fromJson(dto.user.data) : null,
    hireDate: dto.hire_date ? DateTime.fromISO(dto.hire_date) : null,
    lastActiveAt: dto.last_active_at ? DateTime.fromISO(dto.last_active_at) : null,
    newAppLoginAt: dto.new_app_login_at ? DateTime.fromISO(dto.new_app_login_at) : null,
    hasToken: dto.has_token ?? false,
    userId: dto.user_id,
    jobTitleId: (dto.job_title_id ?? null) as JobTitleID | null,
    defaultJobRoleId: (dto.default_job_role_id ?? null) as JobRoleID | null,
    defaultJobRole: dto.default_job_role?.data ? jobRoleFromDto(dto.default_job_role.data) : null,
}))
